import api from './api'
import type {
  ApplicationSubmissionResponse,
  ApplicationListResponse,
  ApplicationDetailResponse,
  ApplicationQueryParams,
  ApplicationStatusUpdateRequest,
  CreateStaffAccountRequest,
  CreateStaffAccountResponse,
  UpdateReferenceVerificationPayload,
  ReferenceEntry,
} from '@/types/application'

export const applicationService = {
  /**
   * Submit an employee application with form data and documents (Public)
   *
   * @param formData FormData containing core fields, JSON strings, and files
   * @returns Promise with submission response
   */
  async submitApplication(formData: FormData): Promise<ApplicationSubmissionResponse> {
    const response = await api.post<ApplicationSubmissionResponse>('/applications', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Get paginated list of applications with filters (Admin)
   */
  async getApplications(params: ApplicationQueryParams = {}): Promise<ApplicationListResponse> {
    const response = await api.get<ApplicationListResponse>('/applications', { params })
    return response.data
  },

  /**
   * Get single application details with documents (Admin)
   */
  async getApplication(id: number): Promise<ApplicationDetailResponse> {
    const response = await api.get<ApplicationDetailResponse>(`/applications/${id}`)
    return response.data
  },

  /**
   * Update application status and admin remarks (Admin)
   */
  async updateApplicationStatus(
    id: number,
    payload: ApplicationStatusUpdateRequest
  ): Promise<ApplicationDetailResponse> {
    const response = await api.put<ApplicationDetailResponse>(`/applications/${id}/status`, payload)
    return response.data
  },

  /**
   * Create a staff account from an approved application (Admin)
   */
  async createStaffAccount(
    id: number,
    payload: CreateStaffAccountRequest = {}
  ): Promise<CreateStaffAccountResponse> {
    const response = await api.post<CreateStaffAccountResponse>(
      `/applications/${id}/create-account`,
      payload
    )
    return response.data
  },

  /**
   * Download submitted application document (Admin)
   */
  async downloadDocument(documentId: number, fileName: string): Promise<void> {
    const response = await api.get(`/applications/documents/${documentId}/download`, {
      responseType: 'blob',
    })

    // Create a temporary link element to trigger browser download
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = fileName || 'document'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },

  /**
   * Download full application summary as PDF (Admin)
   */
  async downloadApplicationPdf(id: number, fileName?: string, params?: Record<string, any>): Promise<void> {
    const response = await api.get(`/applications/${id}/pdf`, {
      params: { download: 1, _t: Date.now(), ...params },
      responseType: 'blob',
    })

    const blob = new Blob([response.data], { type: 'application/pdf' })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = fileName || `Jeducare_Application_${id}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },

  /**
   * Get raw PDF blob for inline preview or printing (Admin)
   */
  async getApplicationPdfBlob(id: number, params?: Record<string, any>): Promise<Blob> {
    const response = await api.get(`/applications/${id}/pdf`, {
      params: { _t: Date.now(), ...params },
      responseType: 'blob',
    })
    return new Blob([response.data], { type: 'application/pdf' })
  },

  /**
   * Create an existing / paper employee application record (Admin)
   */
  async createExistingRecord(formData: FormData): Promise<ApplicationDetailResponse> {
    const response = await api.post<ApplicationDetailResponse>('/applications/existing', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Upload additional documents to an application (Admin)
   */
  async uploadDocuments(
    id: number,
    formData: FormData
  ): Promise<{ success: boolean; message: string; data: any[] }> {
    const response = await api.post<{ success: boolean; message: string; data: any[] }>(
      `/applications/${id}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  /**
   * Get blob for previewing an uploaded application document (inline PDF or image)
   */
  async previewDocumentBlob(documentId: number): Promise<Blob> {
    const response = await api.get(`/applications/documents/${documentId}/preview`, {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Get preview URL string for an uploaded document
   */
  getDocumentPreviewUrl(documentId: number): string {
    return `/api/applications/documents/${documentId}/preview`
  },

  /**
   * Resend applicant secure edit access link (Admin)
   */
  async resendAccessLink(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/applications/${id}/resend-access-link`)
    return response.data
  },

  /**
   * Revoke applicant secure edit access link and active sessions (Admin)
   */
  async revokeAccess(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/applications/${id}/revoke-access`)
    return response.data
  },

  /**
   * Update verification status and notes for an applicant's employment reference (Admin)
   */
  async updateReferenceVerification(
    id: number,
    payload: UpdateReferenceVerificationPayload
  ): Promise<ApplicationDetailResponse & { reference?: ReferenceEntry }> {
    const response = await api.put<ApplicationDetailResponse & { reference?: ReferenceEntry }>(
      `/applications/${id}/references`,
      payload
    )
    return response.data
  },

  /**
   * Delete an employee application record and its documents (Admin)
   */
  async deleteApplication(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(`/applications/${id}`)
    return response.data
  },
}

export default applicationService
